import type { InsuranceTiers, ValoremRange } from '#root/models/Shipping';
import roundDecimal from './roundDecimal';

/** Paso del slider de valorización, en soles. */
const VALOREM_STEP = 10;

/** Costo del seguro con IGV, redondeado a 2 decimales. 0 si no se asegura. */
export const insuranceCost = (valorem: number, rate: number, igv: number): number =>
  valorem > 0 ? roundDecimal(valorem * rate * (1 + igv)) : 0;

/** Tarifa según el total del envío: 0 bajo el mínimo; si no, el primer tramo (up_to inclusivo). */
export const rateForTotal = (total: number, tiers: InsuranceTiers): number => {
  if (total < tiers.min_enabled) return 0;
  const tier = tiers.rates.find((item) => item.up_to === null || total <= item.up_to);
  return tier ? tier.rate : 0;
};

/** Costo estimado del seguro de un grupo: mismo cálculo que el backend (tope, tarifa del total e IGV). */
export const estimateCost = (valoremTotal: number, total: number, tiers: InsuranceTiers): number =>
  insuranceCost(Math.min(valoremTotal, tiers.cap), rateForTotal(total, tiers), tiers.igv);

/** Seguro inicial de un acuerdo sin preferencia guardada: el máximo de su rango (0 si no admite). */
export const defaultValorem = (range: ValoremRange): number => (range.enabled ? range.max : 0);

/** Monto acotado al rango [min, max]. */
export const clampValorem = (v: number, min: number, max: number): number =>
  Math.min(Math.max(v, min), max);

/*
 * Slider por índice: el Slider de MUI v4 redondea a min + k·step y recién después acota a max,
 * así que si (max − min) no es múltiplo del paso nunca llega al máximo exacto. Con índices
 * 0..N (step 1) Home/End/flechas/arrastre llegan a los dos extremos; el último índice es max.
 */

/** Cantidad de pasos N (índices 0..N). 0 si min = max: no se monta el slider. */
export const valoremSteps = (min: number, max: number, step: number = VALOREM_STEP): number =>
  max > min ? Math.ceil((max - min) / step) : 0;

/** Índice → monto en soles. */
export const valoremFromIndex = (
  k: number,
  min: number,
  max: number,
  step: number = VALOREM_STEP,
): number => (k >= valoremSteps(min, max, step) ? max : min + k * step);

/** Monto en soles → índice. Un monto fuera de la grilla de 10 queda entre dos índices. */
export const valoremToIndex = (
  v: number,
  min: number,
  max: number,
  step: number = VALOREM_STEP,
): number => {
  if (v >= max) return valoremSteps(min, max, step);
  if (v <= min) return 0;
  return (v - min) / step;
};
